// Stage 97: WARP — widget-scale CSS-3D warp tunnel, kind-aware.
//
// Each Bite in the current turn gets ONE billboard that cycles through
// z-space. Visual treatment is driven by:
//   - bite.kind  (OBSERVATION | QUESTION | OBJECTION) → hue
//   - swat.tag   (ACCEPT | REJECT | DEFER | implicit reject) → motion
//
// The scene survives turn boundaries by full-sweeping on turnId change:
// previous billboards rapidly recede and fade, new ones enter from far Z.
//
// Direct port of WARP-Zero-Design/ui_kits/widget/WarpScene.jsx with
// TS prop typing, explicit React imports, and host-driven turnState.

import { useEffect, useMemo, useRef } from 'react';
import type { Bite, BiteKind, SwatEntry } from '../../api/gadfly';
import { KIND_COLORS } from './kindMeta';

const Z_NEAR = 80;
const Z_FAR = -900;

interface BillboardSlot {
  node: HTMLDivElement;
  state: {
    angle: number;
    radius: number;
    z: number;
    baseOpacity: number;
    baseVelocity: number;
    exiting: boolean;
    implicit: boolean;
    swatTag: string | null;
    // Stage 201-X: true when a Laughter engram fired for this bite this
    // turn. Drives the additive lime glow (CSS data-laugh-fired="1").
    laughFired: boolean;
  };
}

interface Particle {
  x: number;
  y: number;
  z: number;
  vz: number;
}

interface WarpSceneProps {
  width?: number;
  height?: number;
  bites: Bite[];
  swats: Record<number, SwatEntry>;
  turnId: string | null;
  failed: boolean;
  failureReason: string | null;
  emptyState: 'silent' | 'failure' | 'disabled' | null;
  speed?: number;
  forward?: boolean;
  /** Stage 201-X: predicate — true when a Laughter engram fired for the
   *  given bite index this turn. Drives the per-billboard lime glow. */
  laughFiredFor?: (biteIndex: number) => boolean;
}

export function WarpScene({
  width = 360,
  height = 220,
  bites,
  swats,
  turnId,
  failed: _failed,
  failureReason,
  emptyState,
  speed = 1.0,
  forward = true,
  laughFiredFor,
}: WarpSceneProps): JSX.Element {
  const sceneRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number | null>(null);

  const speedRef = useRef(speed);
  const targetSpeedRef = useRef(speed);
  useEffect(() => {
    targetSpeedRef.current = speed;
  }, [speed]);
  const forwardRef = useRef(forward);
  useEffect(() => {
    forwardRef.current = forward;
  }, [forward]);

  // billboard slots keyed by bite.index
  const slotsRef = useRef<Map<number, BillboardSlot>>(new Map());
  // when turnId changes, flag everything exiting before new bites land
  const lastTurnRef = useRef<string | null>(turnId);
  useEffect(() => {
    if (lastTurnRef.current && lastTurnRef.current !== turnId) {
      slotsRef.current.forEach((slot) => {
        slot.state.exiting = true;
      });
    }
    lastTurnRef.current = turnId;
  }, [turnId]);

  // ---- particles (one-time setup) -------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const PARTICLES = 220;
    particlesRef.current = [];
    for (let i = 0; i < PARTICLES; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * 140;
      particlesRef.current.push({
        x: Math.cos(a) * r,
        y: Math.sin(a) * r,
        z: Z_FAR + Math.random() * (Z_NEAR - Z_FAR),
        vz: 1.5 + Math.random() * 1.5,
      });
    }
  }, [width, height]);

  // ---- main rAF loop --------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas ? canvas.getContext('2d') : null;
    const LERP = 0.05;

    const tick = (): void => {
      // lerp speed
      speedRef.current +=
        (targetSpeedRef.current - speedRef.current) * LERP;
      const s = speedRef.current;
      const fwd = forwardRef.current;

      // ----- billboards ----------------------------------------------
      slotsRef.current.forEach((slot) => {
        const { node, state } = slot;
        if (!node) return;

        const swat = state.swatTag;
        let vel = state.baseVelocity;
        let scaleMul = 1;

        if (swat === 'ACCEPT') {
          vel *= 0.45;
          scaleMul = 1.2;
        } else if (swat === 'DEFER') {
          vel *= 0.3;
          scaleMul = 0.95;
        } else if (swat === 'REJECT') {
          vel *= 1.5;
        } else if (state.exiting) {
          vel *= 2.2;
        } else if (state.implicit) {
          vel *= 0.7;
        }

        state.z += (fwd ? 1 : -1) * vel * s;

        if (swat === 'DEFER') {
          if (state.z > -160) state.z = -160 - (state.z - -160) * 0.5;
          if (state.z < -260) state.z = -260;
        } else if (state.exiting) {
          if (state.z > Z_NEAR + 80 || state.z < Z_FAR - 80) {
            node.style.opacity = '0';
            return;
          }
        } else if (fwd && state.z > Z_NEAR + 20) {
          state.z = Z_FAR + Math.random() * 60;
        } else if (!fwd && state.z < Z_FAR - 20) {
          state.z = Z_NEAR - Math.random() * 60;
        }

        const norm = (state.z - Z_FAR) / (Z_NEAR - Z_FAR);
        const baseScale = Math.max(0.6, Math.min(1.25, 1.0 / Math.sqrt(s)));
        const scale = baseScale * scaleMul;
        const x = Math.cos(state.angle) * state.radius;
        const y = Math.sin(state.angle) * state.radius * 0.5;

        let opacity = state.baseOpacity * (0.3 + norm * 0.9);
        if (swat === 'ACCEPT') opacity = Math.min(1, opacity * 1.4 + 0.1);
        if (swat === 'DEFER') opacity *= 0.55;
        if (swat === 'REJECT') opacity *= 0.22;
        if (state.exiting) opacity *= 0.5;
        if (state.implicit) opacity *= 0.3;
        // Stage 201-X: subtle additive lift; the CSS data-attr glow does
        // the primary visual work.
        if (state.laughFired) opacity = Math.min(1, opacity * 1.15 + 0.05);

        node.style.transform = `translate3d(${x}px, ${y}px, ${state.z}px) translate(-50%,-50%) scale(${scale})`;
        node.style.opacity = opacity.toFixed(3);
      });

      // ----- particles ------------------------------------------------
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
        const cx = width / 2;
        const cy = height / 2;
        const dominant = pickDominantKind(bites);
        const rgb = (KIND_COLORS[dominant] || KIND_COLORS.OBSERVATION).rgb;
        ctx.fillStyle = `rgba(${rgb}, 0.85)`;
        ctx.globalCompositeOperation = 'lighter';

        const activeCount = Math.floor(Math.min(220, 30 + s * 220));
        const parts = particlesRef.current;
        for (let i = 0; i < parts.length; i++) {
          const p = parts[i];
          if (i >= activeCount) continue;
          p.z += (fwd ? 1 : -1) * p.vz * s;
          if (fwd && p.z > Z_NEAR + 40) p.z = Z_FAR;
          else if (!fwd && p.z < Z_FAR - 40) p.z = Z_NEAR;
          const persp = 600 / (600 - p.z);
          const sx = cx + p.x * persp;
          const sy = cy + p.y * persp * 0.5;
          const r = Math.max(0.4, 1.6 * persp);
          const norm = (p.z - Z_FAR) / (Z_NEAR - Z_FAR);
          ctx.globalAlpha = 0.1 + norm * 0.55;
          ctx.beginPath();
          ctx.arc(sx, sy, r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [width, height, bites]);

  // ---- reconcile billboard DOM with bites + swats --------------------
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const wanted = new Set(bites.map((b) => b.index));

    slotsRef.current.forEach((slot, idx) => {
      if (!wanted.has(idx)) slot.state.exiting = true;
    });

    bites.forEach((bite, i) => {
      let slot = slotsRef.current.get(bite.index);
      if (!slot) {
        const node = document.createElement('div');
        node.className = 'wz-billboard';
        scene.appendChild(node);
        const angle = (i / 14) * Math.PI * 2 + Math.random() * 0.4;
        const radius = 60 + (i % 3) * 14 + Math.random() * 10;
        const z = Z_FAR + 60 + Math.random() * 100;
        const baseOp = 0.55 + Math.random() * 0.4;
        const vel = 1.7 + Math.random() * 1.0;
        slot = {
          node,
          state: {
            angle,
            radius,
            z,
            baseOpacity: baseOp,
            baseVelocity: vel,
            exiting: false,
            implicit: false,
            swatTag: null,
            laughFired: false,
          },
        };
        slotsRef.current.set(bite.index, slot);
      }
      slot.node.dataset.kind = bite.kind;
      const hex = (KIND_COLORS[bite.kind] || KIND_COLORS.OBSERVATION).hex;
      slot.node.style.setProperty('--kind-color', hex);
      slot.node.style.color = hex;
      slot.node.textContent = `[${bite.kind[0]}] ${bite.text}`;

      const swat = swats[bite.index];
      const tag = swat && swat.tag ? swat.tag : null;
      slot.state.swatTag = tag;
      slot.node.dataset.swat = tag || '';
      slot.state.implicit = false;
    });
  }, [bites, swats]);

  // ---- Stage 201-X: apply the laugh-fired flag to each live slot -------
  // Runs whenever the predicate identity changes (BiteWidget memoizes it
  // over the pulse + turnId). Sets state.laughFired (read by the rAF loop
  // for the opacity bump) and the data-laugh-fired attribute (read by the
  // CSS additive glow). New slots created after a pulse are picked up the
  // next time this effect runs because laughFiredFor identity changed.
  useEffect(() => {
    slotsRef.current.forEach((slot, idx) => {
      const fired = laughFiredFor ? laughFiredFor(idx) : false;
      slot.state.laughFired = fired;
      if (slot.node) slot.node.dataset.laughFired = fired ? '1' : '0';
    });
  }, [laughFiredFor, bites]);

  const placeholderText = useMemo(() => {
    if (emptyState === 'failure')
      return `⚠ ${failureReason || 'gadfly error'}`;
    if (emptyState === 'silent') return '▸ silent turn — no bites';
    if (emptyState === 'disabled') return '▸ gadfly module offline';
    return null;
  }, [emptyState, failureReason]);

  return (
    <div className="wz-scene-shell" style={{ width, height }}>
      <canvas
        ref={canvasRef}
        className="wz-particles"
        style={{ width, height }}
      />
      <div ref={sceneRef} className="wz-scene" />
      <div className="wz-vignette" />
      <div className="wz-crosshair">
        <span />
        <span />
        <span />
        <span />
      </div>
      {placeholderText ? (
        <div
          className={`wz-placeholder ${
            emptyState === 'failure' ? 'is-failure' : ''
          }`}
        >
          {placeholderText}
        </div>
      ) : null}
    </div>
  );
}

function pickDominantKind(bites: Bite[]): BiteKind {
  if (!bites || bites.length === 0) return 'OBSERVATION';
  const counts: Record<BiteKind, number> = {
    OBSERVATION: 0,
    QUESTION: 0,
    OBJECTION: 0,
  };
  bites.forEach((b) => {
    counts[b.kind] = (counts[b.kind] || 0) + 1;
  });
  let best: BiteKind = 'OBSERVATION';
  let n = -1;
  for (const k of Object.keys(counts) as BiteKind[]) {
    if (counts[k] > n) {
      best = k;
      n = counts[k];
    }
  }
  return best;
}
